'use client';

import React from 'react';
import { cn } from '@/lib/cn';
import { IconSearch, IconChevronLeft, IconDeleteCircle, IconLocationPin } from '@repo/svgs';
import ListCell, { ListCellSkeleton } from '@/components/ui/ListCell';
import useBuildingAutocomplete from '@/features/buildings/hooks/useBuildingAutocomplete';
import useNavigateToSearchedBuilding from '@/features/buildings/hooks/useNavigateToSearchedBuilding';
import { getSearchItemApi } from '@repo/api';
import { useAnalytics } from '@/hooks/useAnalytics';
import { createPortal } from 'react-dom';
import {
  getRecentSearches,
  saveRecentSearch,
  type RecentBuildingSearch,
} from '@/utils/recentSearches';
import { BuildingSearchHistoryList } from './components/BuildingSearchHistoryList';
import { useStore } from '@/providers/StoreProvider';

export function BuildingsSearchBarMobile({
  className,
  eventScreen,
}: {
  className?: string;
  eventScreen?: 'home';
}) {
  const [open, setOpen] = React.useState(false);
  // Tracks whether the input is focused; drives the accent (active) border.
  const [isFocused, setIsFocused] = React.useState(true);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const headerRef = React.useRef<HTMLDivElement | null>(null);
  const [recentSearches, setRecentSearches] = React.useState<RecentBuildingSearch[]>([]);
  const { query, setQuery, suggestions, isLoading, sessionId } = useBuildingAutocomplete(
    undefined,
    {
      minLength: 4,
      debounceMs: 300,
    },
  );
  const analytics = useAnalytics();
  const navigateToSearchedBuilding = useNavigateToSearchedBuilding();
  const isLoggedIn = useStore(s => s.webAuth.isLoggedIn);

  const openBuildingPage = async (placeId: string) => {
    analytics.track('header_address_search_select', { type: eventScreen ?? 'home' });
    try {
      const building = await getSearchItemApi(placeId, sessionId, {
        credentials: isLoggedIn ? 'include' : 'omit',
      });
      saveRecentSearch(building);
      // NYC -> listings page with this address as the location; other cities -> building page.
      navigateToSearchedBuilding(building);
    } catch (err) {
      // Most often a 404 — the autocomplete suggested a place that
      // doesn't have a matching building record. Silent fail keeps the
      // dropdown open so the user can pick a different suggestion.
      // Matches the behavior of the desktop search bar.
      // eslint-disable-next-line no-console
      console.error('Failed to open building from search:', err);
    }
  };

  const openRecentBuilding = React.useCallback(
    (recent: RecentBuildingSearch) => {
      analytics.track('header_address_search_select', { type: eventScreen ?? 'home' });
      saveRecentSearch(recent);
      // NYC -> listings page with this address as the location; other cities -> building page.
      navigateToSearchedBuilding(recent);
    },
    [analytics, eventScreen, navigateToSearchedBuilding],
  );

  React.useEffect(() => {
    if (open) {
      setIsFocused(true);
      inputRef.current?.focus();
      setRecentSearches(getRecentSearches().slice(0, 6));
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  if (!open) {
    return (
      <div className={cn(className)}>
        <button
          type="button"
          onClick={() => {
            eventScreen && analytics.track('header_address_search', { type: eventScreen });
            setOpen(true);
          }}
          className={cn(
            'flex w-full items-center gap-2 rounded-full border border-secondary bg-primary px-4 py-3 col-span-full',
            'active:scale-[0.99] transition shadow',
          )}
        >
          <IconSearch className="icon-tertiary" width={16} height={16} />
          <span className="text-body-2 text-tertiary">Search address or building</span>
        </button>
      </div>
    );
  }

  return createPortal(
    <div role="dialog" aria-modal="true" className="fixed h-dvh inset-0 z-50 bg-primary md:hidden">
      <div ref={headerRef} className="sticky top-0 z-[1] bg-primary px-4 py-4">
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Back"
            onClick={() => setOpen(false)}
            className="inline-flex items-center justify-center rounded-full p-3"
          >
            <IconChevronLeft className="icon-secondary" width={16} height={16} />
          </button>

          <div
            onClick={() => inputRef.current?.focus()}
            className={cn(
              'flex min-h-[44px] flex-1 items-center gap-2 rounded-full px-4 bg-primary',
              // Active (focused) state: accent border. Default state once the
              // user taps outside the input: regular secondary border.
              isFocused ? 'border-2 border-accent' : 'border border-secondary',
              'animate-slide-up',
              'transition-all duration-50 ease-out',
            )}
          >
            <IconSearch className="icon-secondary" width={16} height={16} />
            <input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder="Search address or building"
              className="flex-1 bg-primary text-body-2 text-primary placeholder:text-tertiary outline-none"
            />
            <button
              type="button"
              aria-label="Clear"
              disabled={query.length === 0}
              // Keep the input focused (and the active state) when clearing.
              onMouseDown={e => e.preventDefault()}
              onClick={() => setQuery('')}
              className={cn(
                'inline-flex items-center justify-center rounded-full',
                'cursor-pointer disabled:cursor-default',
                // 36px square icon button per DS
                'disabled:opacity-0',
                'h-9 w-9 p-2',
              )}
            >
              <IconDeleteCircle className="icon-secondary" width={16} height={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Hardcoded height of topbar where search bar lies (48px) and padding (28px) */}
      <div className="px-4 pb-8 overflow-y-auto max-h-[calc(100dvh-76px)]">
        {(query?.trim().length ?? 0) <= 3 && recentSearches.length > 0 ? (
          <div className="py-1">
            <BuildingSearchHistoryList
              recentSearches={recentSearches}
              onSelect={openRecentBuilding}
              variant="mobile"
            />
          </div>
        ) : null}

        {(query?.trim().length ?? 0) > 3 && (
          <div className="py-1">
            {isLoading ? (
              <div className="">
                {[1, 2, 3].map(i => (
                  <ListCellSkeleton
                    key={i}
                    className="rounded-none shadow-none border-b border-tertiary"
                  />
                ))}
              </div>
            ) : suggestions.length > 0 ? (
              <div className="">
                {suggestions.map(s => (
                  <ListCell
                    key={s.placeId}
                    title={s.address}
                    {...(s.borough ? { subtitle: s.borough } : {})}
                    leadingIcon={<IconLocationPin className="w-6 h-6 icon-secondary" />}
                    onClick={() => openBuildingPage(s.placeId)}
                    className="rounded-none shadow-none border-b border-tertiary"
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 py-4 text-center">
                <p className="text-body-1-high text-primary">No buildings found</p>
                <p className="text-body-2-normal text-secondary">
                  Try searching a building’s address (e.g., 200 Water St) or name (e.g., Silver
                  Towers).
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
